import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    fetchPublicEvents,
    getPublicEventPath,
} from '../../../services/publicEventsApi';
import { richTextToPlain } from '../../../utils/richText';

const SearchPopup = ({ isPopup, handlePopup }) => {
    const navigate = useNavigate();
    const [query, setQuery] = useState('');
    const [allEvents, setAllEvents] = useState([]);
    const [isLoading, setIsLoading] = useState(false);

    useEffect(() => {
        if (!isPopup) return;
        let cancelled = false;
        (async () => {
            setIsLoading(true);
            try {
                const { events } = await fetchPublicEvents({ limit: 200 });
                if (!cancelled) setAllEvents(Array.isArray(events) ? events : []);
            } catch {
                if (!cancelled) setAllEvents([]);
            } finally {
                if (!cancelled) setIsLoading(false);
            }
        })();
        return () => {
            cancelled = true;
        };
    }, [isPopup]);

    const normalized = useMemo(() => query.trim().toLowerCase(), [query]);
    const results = useMemo(() => {
        if (!normalized) return [];
        return allEvents
            .filter((e) => {
                const title = String(e?.title || '').toLowerCase();
                const desc = richTextToPlain(e?.description).toLowerCase();
                const location = String(e?.location || '').toLowerCase();
                return (
                    title.includes(normalized) ||
                    desc.includes(normalized) ||
                    location.includes(normalized)
                );
            })
            .slice(0, 6);
    }, [allEvents, normalized]);

    const panelStyle = {
        marginTop: 12,
        maxHeight: 280,
        overflowY: 'auto',
        background: 'rgba(8, 12, 26, 0.96)',
        border: '1px solid rgba(148, 163, 184, 0.35)',
        borderRadius: 12,
        padding: 10,
        backdropFilter: 'blur(2px)',
    };

    const goToEvent = (event) => {
        if (!event?.id) return;
        handlePopup();
        navigate(getPublicEventPath(event));
    };

    const handleSubmit = (e) => {
        e.preventDefault();
        if (!normalized) return;
        if (results.length > 0) {
            goToEvent(results[0]);
            return;
        }
        handlePopup();
        navigate(`/events?q=${encodeURIComponent(query.trim())}`);
    };

    return (
        <div id="search-popup" className={`search-popup ${isPopup ? "active" : ""}`}>
            <div className="search-popup__overlay search-toggler" onClick={handlePopup} />
            <div className="search-popup__content">
                <form onSubmit={handleSubmit}>
                    <label htmlFor="search" className="sr-only">search here</label>
                    <input
                        type="text"
                        id="search"
                        placeholder="Rechercher un événement..."
                        value={query}
                        onChange={(e) => setQuery(e.target.value)}
                    />
                    <button type="submit" aria-label="search submit" className="thm-btn">
                        <i className="fas fa-search" />
                    </button>
                </form>
                {isLoading && <p style={{ marginTop: 12, color: '#e2e8f0' }}>Chargement...</p>}
                {!isLoading && normalized && (
                    <div style={panelStyle}>
                        {results.length === 0 ? (
                            <p style={{ color: '#e2e8f0', margin: 8 }}>Aucun résultat.</p>
                        ) : (
                            results.map((ev) => (
                                <button
                                    key={ev.id}
                                    type="button"
                                    onClick={() => goToEvent(ev)}
                                    style={{
                                        width: '100%',
                                        textAlign: 'left',
                                        border: '1px solid rgba(148, 163, 184, 0.35)',
                                        background: 'rgba(15, 23, 42, 0.78)',
                                        color: '#fff',
                                        padding: '9px 10px',
                                        marginBottom: 8,
                                        borderRadius: 8,
                                        cursor: 'pointer',
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: 10,
                                    }}
                                >
                                    <img
                                        src={ev.poster_url || '/images/events/309X166.jpg'}
                                        alt={ev.title || 'event'}
                                        style={{
                                            width: 52,
                                            height: 52,
                                            objectFit: 'cover',
                                            borderRadius: 8,
                                            border: '1px solid rgba(148, 163, 184, 0.4)',
                                            flexShrink: 0,
                                        }}
                                    />
                                    <div style={{ minWidth: 0 }}>
                                        <strong style={{ display: 'block', color: '#f8fafc' }}>
                                            {ev.title}
                                        </strong>
                                        <div style={{ fontSize: 12, color: '#cbd5e1', marginTop: 2 }}>
                                            {ev.location || 'Lieu non défini'}
                                        </div>
                                    </div>
                                </button>
                            ))
                        )}
                    </div>
                )}
            </div>
        </div>
    );
};

export default SearchPopup;
